import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusBanner";
import { WidgetError } from "@/components/WidgetError";
import { fmtDate } from "@/lib/format";

export default function CustomerDashboard() {
  const [meState, setMeState] = useState({ status: "loading", data: null, error: null });
  const [recentState, setRecentState] = useState({ status: "loading", items: [], error: null });

  const loadMe = useCallback(async () => {
    setMeState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get("/portal/me");
      setMeState({ status: "ok", data, error: null });
    } catch (err) {
      setMeState({ status: "error", data: null, error: err });
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setRecentState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get("/portal/orders", { params: { limit: 5 } });
      setRecentState({ status: "ok", items: data.items || [], error: null });
    } catch (err) {
      setRecentState({ status: "error", items: [], error: err });
    }
  }, []);

  useEffect(() => {
    loadMe();
    loadRecent();
  }, [loadMe, loadRecent]);

  const me = meState.data;

  return (
    <div>
      <div className="mb-6">
        <div className="label-overline">Welcome</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">{me?.customer?.display_name || "Customer"}</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Your activity at <b>{me?.tenant?.business_name || "—"}</b>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Account code</div>
          <div className="mt-2 font-mono text-2xl font-black">{me?.customer?.customer_code || "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Signed in as</div>
          <div className="mt-2 font-mono text-sm">{me?.user?.email}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Truck stop</div>
          <div className="mt-2 font-mono text-sm">{me?.tenant?.slug}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 label-overline">Recent orders</div>
        {recentState.status === "error" ? (
          <div className="p-4">
            <WidgetError
              error={recentState.error}
              onRetry={loadRecent}
              title="Could not load your recent orders"
              testId="portal-recent-error"
            />
          </div>
        ) : (
          <table className="tbl w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-overline text-slate-600">
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-left px-4 py-2">Registration</th>
                <th className="text-left px-4 py-2">Service</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Opened</th>
              </tr>
            </thead>
            <tbody>
              {recentState.status === "loading" && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {recentState.status === "ok" && recentState.items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No orders yet</td></tr>
              )}
              {recentState.status === "ok" && recentState.items.map((o) => (
                <tr key={o.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono">
                    <Link to={`/portal/orders/${o.id}`} className="text-blue-700 hover:underline">{o.order_no}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono">{o.registration_no}</td>
                  <td className="px-4 py-2">{o.service_type_snapshot?.label}</td>
                  <td className="px-4 py-2"><StatusPill status={o.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtDate(o.opened_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
